import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { Button } from './Button';

/**
 * The link the customer opens.
 *
 * The email carries it automatically, but the two shops that most need it are the ones the
 * email cannot help: no Resend key yet (every shop, until a human sets one up), or a customer
 * with no email address on file. Both end with somebody wanting to paste a link into their
 * own email, a text, or a WhatsApp message — and until this row existed the link was minted,
 * stored, and invisible.
 *
 * The button MINTS on first press rather than the link simply being there, so an invoice
 * becomes addressable from the internet only because somebody decided it should.
 */
export function CustomerLinkRow({
  kind,
  id,
  url,
}: {
  /**
   * Which surface. All three mint the same kind of token — `public-link.ts` holds the rules
   * once — so only the procedure and the wording differ.
   */
  kind: 'invoice' | 'quote' | 'report';
  id: string;
  url: string | null;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDone = {
    onSuccess: (result: { url: string }) => {
      setError(null);
      void copy(result.url);
      void qc.invalidateQueries();
    },
    onError: (e: { message: string }) => setError(e.message),
  };
  // Both hooks are declared unconditionally — a hook behind an `if` is a crash waiting for
  // the first page that renders the other kind.
  const createInvoice = useMutation(
    trpc.invoice.createPublicLink.mutationOptions(onDone),
  );
  const createQuote = useMutation(trpc.quote.createPublicLink.mutationOptions(onDone));
  const createReport = useMutation(trpc.job.createReportLink.mutationOptions(onDone));
  const create =
    kind === 'invoice' ? createInvoice : kind === 'quote' ? createQuote : createReport;
  const ask = () => {
    if (kind === 'invoice') return createInvoice.mutate({ invoiceId: id });
    if (kind === 'quote') return createQuote.mutate({ quoteId: id });
    return createReport.mutate({ jobId: id });
  };
  /**
   * ⚠️ The report's wording is its own, because the ACT is different. An invoice link shows
   * a document that is already finished; a job report is a page that keeps changing and
   * that the customer can sign off on. "Copy a link" would undersell what the shop is about
   * to hand out.
   */
  const askLabel =
    kind === 'report'
      ? 'Share progress with the customer'
      : 'Copy a link for the customer';

  async function copy(value: string) {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be refused; the link is on screen either way.
      setCopied(false);
    }
  }

  if (!url) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" disabled={create.isPending} onClick={ask}>
          {create.isPending ? 'Making a link…' : askLabel}
        </Button>
        {error ? (
          <span className="text-sm text-[var(--sw-danger-text)]">{error}</span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <code className="h-scroll min-w-0 flex-1 whitespace-nowrap rounded-[var(--radius-sm)] border border-border bg-surface-2 px-3 py-2 text-xs">
        {url}
      </code>
      <Button variant="ghost" onClick={() => void copy(url)}>
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}
